import { Injectable } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { UserService } from './user.service';

@Injectable({
	providedIn: 'root'
})
export class ValidatorService {
	public emailRegex = /^(("")(""[^"]+?""@)|(([_0-9a-z]((\.(?!\.))|[-!#$%&'*+/=?^`{}|~\w])*)@))((\[)(\[(\d{1,3}\.){3}\d{1,3}])|(([0-9a-z][-\w]*[0-9a-z]*\.)+[a-z0-9]{2,17}))$/i;

	constructor(private _userService: UserService) {
	}

	emailPhoneValid(): ValidatorFn | any {
		return (control: any) => {
			const user = this._userService.getUser();
			// This regex was supplied by WS so to match their enforcement but had to be slightly modified for JS as it is not supported as is, this original is commented out for reference
			// ^(?("")(""[^""]+?""@)|(([0-9a-z]((\.(?!\.))|[-!#\$%&'\*\+/=\?\^`\{\}\|~\w])*)(?<=[0-9a-z])@))(?(\[)(\[(\d{1,3}\.){3}\d{1,3}\])|(([0-9a-z][-\w]*[0-9a-z]*\.)+[a-z0-9]{2,17}))$
			if (control.value?.length > 0) {
				if (!user?.globalSettings?.isTextMessagingEnabled) {
					return this.emailRegex.test(control.value) ? null : {
						invalidEmail: true
					};
				} else {
					if (this.emailRegex.test(control.value) || this.isValidPhone(control.value)) {
						return null;
					} else {
						return {
							invalidEmail: true
						}
					}
				}
			} else {
				return null;
			}
		}
	}

	/**
	 * Remove whitespace from a notification recipient that is a phone number, so what we send stays
	 * inside what the server will accept.
	 *
	 * isValidPhone below reduces a number to its digits with replace(/[^0-9]/g, ''), so it treats a
	 * space as formatting and accepts '(602) 123-4567'. The server's own phone check strips only
	 * ( ) - . + , and NOT whitespace, so that same number fails its numeric parse and the save comes
	 * back as 400 'The field emailPod is invalid'. Normalising on the way out closes that gap without
	 * the user having to retype anything.
	 *
	 * An entry containing '@' is returned untouched. It is an e-mail address, and dropping whitespace
	 * out of one would change the address rather than reformat it -- the server checks e-mail strictly
	 * and should keep rejecting a malformed one.
	 */
	static stripPhoneWhitespace(entry: string): string {
		if (!entry || entry.includes('@')) {
			return entry;
		}
		return entry.replace(/\s/g, '');
	}

	isValidPhone(phone): boolean {
		// This came from BE to validate the phone number, but it does NOT match what the server does: this
		// reduces the value to its digits, so it treats a space as formatting and accepts '(602) 265-8888',
		// while the server strips only ( ) - . + , and rejects that same number. stripPhoneWhitespace above
		// exists to close that gap on the way out; do not assume the two agree.
		const NRegEx = /^[2-9]\d{2}$/;
		const XXXXRegEx = /^\d{4}$/;
		let strippedNumber = phone.replace(/[^0-9]/g, '');
		// Check if there is a leading '1' in the phone number
		if (strippedNumber.length == 11 && strippedNumber.substring(0, 1) == '1') {
			// Remove the leading '1' from the stripped phone number
			strippedNumber = strippedNumber.substring(1, 11);
		} else if (strippedNumber.length != 10) {
			// If the phone number doesn't have a leading one
			// then it must be 10-digits long to be a valid phone number.
			return false;
		}

		// Get the Number Plan Area Code (Area Code) from the
		// phone number (first 3-digits of phone number)
		const NPA = strippedNumber.substring(0, 3);

		// Get the Exchange Code from the phone number (middle 3-digits of phone number)
		const NXX = strippedNumber.substring(3, 6);

		// Get the Subscriber Number from the phone number (last 4-digits of phone number)
		const XXXX = strippedNumber.substring(6, 10);

		// Check if the Number Plan Area Code, Exchange Code,
		// and Subscriber Number match the appropriate patterns
		if (!NRegEx.test(NPA) || !NRegEx.test(NXX) || !XXXXRegEx.test(XXXX)) {
			// One of the fields do not match; invalid phone number
			return false;
		} else if (NPA.substring(0, 2) == '37' || NPA.substring(0, 2) == '96') {
			// Numbering Plan Area Codes (Area Code) beginning with 37 or 96 (Area Codes 37X and 96X)
			// are reserved by the Industry Numbering Committee (INC) for unanticipated future purposes
			return false;
		} else if (NXX.substring(1, 2) == '11' && (parseInt(NXX, 10) >= 211 && parseInt(NXX, 10) <= 911)) {
			// If the Exchange Code is 211, 311, 411, 511, 611, 711, 811, or 911, then the Exchange Code is invalid
			return false;
		} else if (NXX == '911') {
			// Exchange Codes 911-916 are invalid Exchange Codes
			// because they are too similar to 911 (emergency services)
			return false;
		} else if (NXX == '700' || NXX == '950' || NXX == '958' || NXX == '959') {
			// Exchange Code 700:         Used by customers to verify their intraLAPA PIC
			// Exchange Code 950:         Used by industry to access Feature Group B
			// Carrier Identification Codes (CIC) and has special Automatic Message Accounting (AMA) triggers
			// Exchange Code 958 & 959:   Used by industry as standard test codes
			return false;
		} else if (NXX == '555') {
			// Check if Exchange Code is '555'
			if (parseInt(XXXX, 10) >= 100 && parseInt(XXXX, 10) <= 199)
				// If Exchange Code is '555' and Subscriber Number is
				// between '0100' and '0199' (inclusive) then phone number is invalid
				return false;
		}

		// If we have reached this point the phone number is valid
		return true;
	}

	maxArrayLength(max: number): ValidatorFn | any {
		return (control: any) => {
			if (!(control.value instanceof Array)) return;
			return control.value.length === max ? {maxLength: true} : null;
		}
	}

	minArrayLength(min: number): ValidatorFn | any {
		return (control: any) => {
			if (!(control.value instanceof Array)) return;
			return control.value.length < min ? {minLength: true} : null;
		}
	}

  phoneNumberMask(userInput: string) {
		if (userInput == null || userInput.length === 0) {
			return null;
		} else {
			const PHONE_REGEX = /^(\+\d{1,2}\s)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\s?[\s.x]?\d{0,5}$/i;

			return PHONE_REGEX.test(userInput) ? true : false

		}
	}
}
